import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * LinkedText — web renderer for parsed note text.
 *
 * Everything about *what* is a link lives in utils/noteText; this file only
 * decides how each segment looks and what a click does. The native twin
 * (LinkedText.native.tsx) renders the same segments with React Native
 * primitives, so a note reads the same on both platforms.
 *
 * Emits inline elements only, so it drops into any existing note container
 * (which supplies `white-space: pre-wrap` for newlines).
 */
import React from 'react';
import { parseNoteText, truncateNoteSegments } from '../../utils/noteText';
// Matches the anchor treatment already used for links in AI chat messages.
const LINK_COLOR = '#6AA0FF';
const linkBase = {
    color: LINK_COLOR,
    textDecoration: 'underline',
    wordBreak: 'break-word',
};
// A reference is a button, not an anchor: it navigates inside the SPA rather
// than to a URL, and the app is query-param routed with no per-entity path.
const referenceBase = {
    ...linkBase,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
};
const mentionBase = {
    display: 'inline-block',
    backgroundColor: '#1E3A5F',
    color: '#60A5FA',
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 500,
    fontSize: '0.95em',
};
const externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' };
export const LinkedText = ({ text, options, maxLength, mentionStyle, linkStyle, onWorkOrderPress, onInvoicePress, onMentionPress, }) => {
    const segments = React.useMemo(() => {
        if (!text)
            return [];
        const parsed = parseNoteText(text, options);
        return maxLength ? truncateNoteSegments(parsed, maxLength) : parsed;
    }, [text, options, maxLength]);
    if (!text)
        return null;
    const renderReference = (segment, key, onPress) => (_jsx("button", { type: "button", onClick: (event) => {
            // Notes live inside clickable rows and cards; a reference click must
            // not also select the row behind it.
            event.stopPropagation();
            event.preventDefault();
            onPress(segment.value);
        }, style: { ...referenceBase, ...linkStyle }, children: segment.text }, key));
    const renderSegment = (segment, index) => {
        const key = `${segment.type}-${index}`;
        switch (segment.type) {
            case 'url':
                return (_jsx("a", { href: segment.value, ...externalLinkProps, onClick: (e) => e.stopPropagation(), style: { ...linkBase, ...linkStyle }, children: segment.text }, key));
            case 'email':
                return (_jsx("a", { href: `mailto:${segment.value}`, onClick: (e) => e.stopPropagation(), style: { ...linkBase, ...linkStyle }, children: segment.text }, key));
            case 'phone':
                return (_jsx("a", { href: `tel:${segment.value}`, onClick: (e) => e.stopPropagation(), style: { ...linkBase, ...linkStyle }, children: segment.text }, key));
            case 'workOrder':
                return onWorkOrderPress ? renderReference(segment, key, onWorkOrderPress) : _jsx("span", { children: segment.text }, key);
            case 'invoice':
                return onInvoicePress ? renderReference(segment, key, onInvoicePress) : _jsx("span", { children: segment.text }, key);
            case 'mention': {
                const chipStyle = { ...mentionBase, ...mentionStyle };
                if (!onMentionPress) {
                    return (_jsx("span", { style: chipStyle, children: segment.text }, key));
                }
                return (_jsx("button", { type: "button", onClick: (event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        onMentionPress(segment.value, segment.text.slice(1));
                    }, style: { ...chipStyle, border: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 500 }, children: segment.text }, key));
            }
            default:
                return _jsx("span", { children: segment.text }, key);
        }
    };
    return _jsx(_Fragment, { children: segments.map(renderSegment) });
};
export default LinkedText;
